<template>
  <div class="bg-base-100 rounded-lg p-6">
    <div
      class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8"
    >
      <h2 class="text-xl font-semibold">Paslaugų gavėjų sąrašas</h2>
      <div class="flex gap-2">
        <label class="input input-neutral input-sm">
          <DefaultIcon name="magnifying" size="w-3 h-3" />
          <input
            type="search"
            class="grow"
            placeholder="Paieška"
            :value="route.query.search || ''"
            @input="handleSearch"
          />
        </label>
        <DataFilter v-model="filters" title="Filtrai">
          <fieldset class="fieldset mb-2">
            <legend class="fieldset-legend text-sm">Paslaugų teikimo pagrindas</legend>
            <select
              v-model="filters.service_type"
              class="select w-full"
            >
              <option value="">Visi tipai</option>
              <option
                v-for="(config, key) in referenceDataStore.clientServiceTypeConfig"
                :key="key"
                :value="key"
              >
                {{ config.text }}
              </option>
            </select>
          </fieldset>
          <fieldset class="fieldset mb-2">
            <legend class="fieldset-legend text-sm">Paslauga galioja iki</legend>
            <input
              type="date"
              v-model="filters.valid_until"
              class="input w-full"
            />
          </fieldset>
        </DataFilter>
        <button
          class="btn btn-sm btn-outline"
          @click="handleExport"
          :disabled="clientStore.isExporting"
          title="Atsisiųsti sąrašą .xlsx formatu"
        >
          <DefaultIcon name="download" size="w-4 h-4" class="mr-1" />
        </button>
        <router-link
          to="/clients/new"
          class="btn btn-sm btn-success"
          title="Įtraukti naują klientą"
        >
          <DefaultIcon name="plus" size="w-4 h-4" />
          Naują
        </router-link>
      </div>
    </div>

    <div v-if="clientStore.isLoading" class="text-center py-4">
      <span class="loading loading-spinner loading-lg"></span>
    </div>

    <div v-else class="overflow-x-auto">
      <div v-if="clientStore.clients.length > 0">
        <table class="table w-full mb-3">
          <thead>
            <tr>
              <th>Vardas</th>
              <th>Pavardė</th>
              <th>El. paštas</th>
              <th>Telefonas</th>
              <th>Adresas</th>
              <th>Paslauga galioja iki</th>
              <th>Pavėžėjimai<br />(viso/neatvyko)</th>
              <th>Pastaba</th>
              <th>Veiksmai</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="client in clientStore.clients" :key="client.id">
              <td>{{ client.name }}</td>
              <td>{{ client.surname }}</td>
              <td>{{ client.email }}</td>
              <td>{{ client.phone }}</td>
              <td>{{ client.address }}</td>
              <td>
                <div class="flex items-center gap-2">
                  <span>{{ client.valid_until }}</span>
                  <span
                    v-if="isValidUntilExpired(client.valid_until)"
                    class="badge badge-error badge-sm"
                  >Pasibaigė</span>
                </div>
                <div
                  v-if="client.service_restricted"
                  class="text-xs text-error"
                >
                  Apribotas paslaugų teikimas iki:
                  {{ client.service_restriction_until }}
                </div>
              </td>
              <td>
                {{ client.trips_count }} / {{ client.trips_count_not_arrived }}
              </td>
              <td>
                <div v-if="client.description" class="text-xs">
                  Aprašymas: {{ client.description }}
                </div>
                <div v-if="client.service_type" class="text-xs">
                  Paslaugų teikimo pagrindas:
                  {{
                    referenceDataStore.clientServiceTypeConfig[
                      client.service_type
                    ]?.text
                  }}
                </div>
              </td>
              <td>
                <div class="flex gap-2">
                  <router-link
                    :to="'/clients/' + client.id + '/edit'"
                    class="btn btn-sm btn-ghost btn-circle"
                    title="Redaguoti įrašą"
                  >
                    <DefaultIcon name="editPencil" size="w-4 h-4" />
                  </router-link>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <Pagination :meta="clientStore.meta" />
      </div>
      <div v-else>
        <div v-if="route.query.search || activeFiltersCount > 0">
          Pagal pasirinktus parametrus paslaugų gavėjų nerasta.
        </div>
        <div v-else>
          Paslaugų gavėjų dar nesukūrėte. Įtraukite naują paslaugų gavėję
          paspausdę - Įtraukti naują. Paslaugų gavėjai automatiškai
          užregistruojami kuriant pavėžėjimų užsakymus.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useClientStore } from "@/stores/clients";
import { useReferenceDataStore } from "@/stores/referenceData";
import { watch, onMounted, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import DefaultIcon from "@/components/icons/DefaultIcon.vue";
import Pagination from "@/components/ui/pagination/PaginationComponent.vue";
import DataFilter from "@/components/filters/DataFilter.vue";

const route = useRoute();
const router = useRouter();
const clientStore = useClientStore();
const referenceDataStore = useReferenceDataStore();

const filters = ref({
  service_type: "",
  valid_until: "",
});

const activeFiltersCount = computed(() =>
  Object.values(filters.value).filter((v) => v).length,
);

let searchTimeout = null;

onMounted(async () => {
  await referenceDataStore.ensureDataLoaded();
});

const loadClients = async () => {
  try {
    const { page = 1, ...params } = route.query;
    await clientStore.fetchClients(page, params);

    // Page no longer exists (e.g. last row on the last page was deleted)
    const lastPage = clientStore.meta?.last_page;
    if (lastPage > 0 && Number(page) > lastPage) {
      router.replace({ query: { ...route.query, page: lastPage } });
    }
  } catch (error) {
    console.error("Failed to fetch clients:", error);
  }
};

// Single watcher for all route changes (runs immediately on mount)
watch(() => route.query, loadClients, { immediate: true });

const handleSearch = (event) => {
  if (searchTimeout) clearTimeout(searchTimeout);

  searchTimeout = setTimeout(() => {
    const value = event.target.value.trim();
    const query = { ...route.query };

    if (value.length >= 3) {
      query.search = value;
      delete query.page;
    } else {
      delete query.search;
      delete query.page;
    }

    router.push({ query });
  }, 300);
};

const handleExport = () => {
  clientStore.exportClients(route.query);
};

const isValidUntilExpired = (validUntil) => {
  if (!validUntil) return false;
  return validUntil <= new Date().toISOString().slice(0, 10);
};
</script>
